/**
 * CourseModulesSection Component
 *
 * For SELF_GUIDED_COURSE rituals - manages video course modules
 * with title, description, duration, and drag-and-drop reordering.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Disc, GripVertical, ImagePlus, Plus, Trash2, Video, X } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { COURSE_MODULE_MEDIA_LIMITS, IMAGE_LIMITS, THUMBNAIL_LIMITS } from "@/constants/media"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { buildAcceptAttribute, isFileTypeAllowed } from "@/utils/fileTypes"

// Built once: allowed types are static module constants.
const COURSE_MODULE_ACCEPT = buildAcceptAttribute([
  ...COURSE_MODULE_MEDIA_LIMITS.VIDEOS.ALLOWED_TYPES,
  ...COURSE_MODULE_MEDIA_LIMITS.AUDIO.ALLOWED_TYPES,
])

export interface CourseModule {
  id: string
  uploadId?: string
  video_url?: string
  title: string
  description: string
  duration: string
  order: number
  isFree: boolean
  videoFile?: File
  videoPreview?: string
  mediaType?: "VIDEO" | "AUDIO"
  thumbnailFile?: File
  thumbnailPreview?: string
  removeThumbnail?: boolean
}

interface CourseModulesSectionProps {
  modules: CourseModule[]
  onAddModule: () => void
  onRemoveModule: (id: string) => void
  onReorderModules: (modules: CourseModule[]) => void
  onUpdateModule: (
    id: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => void
  moduleErrors?: Record<string, { title?: string; description?: string; video?: string }>
  sectionError?: string | null
  thumbnailFiles?: Record<string, File>
}

// Sortable Module Component
function SortableModule({
  module,
  index,
  onRemoveModule,
  onRemoveVideo,
  onUpdateModule,
  videoInputRef,
  thumbnailInputRef,
  currentModuleIdRef,
  currentThumbnailModuleIdRef,
  errors,
}: {
  module: CourseModule
  index: number
  onRemoveModule: (id: string) => void
  onRemoveVideo: (id: string) => void
  onUpdateModule: (
    id: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => void
  videoInputRef: React.RefObject<HTMLInputElement | null>
  thumbnailInputRef: React.RefObject<HTMLInputElement | null>
  currentModuleIdRef: React.RefObject<string | null>
  currentThumbnailModuleIdRef: React.RefObject<string | null>
  errors?: { title?: string; description?: string; video?: string }
}) {
  const { t } = useTranslation("rituals")
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: module.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border rounded-lg p-4 bg-card space-y-4"
    >
      {/* Module Header - Drag handle and order number */}
      <div className="flex items-center gap-3">
        <GripVertical
          {...attributes}
          {...listeners}
          className="text-body flex-shrink-0 cursor-grab active:cursor-grabbing"
          size={20}
        />
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="font-semibold text-sm text-primary">{index + 1}</span>
        </div>
        <span className="font-semibold text-primary">
          {t("courseModules.module", { index: index + 1 })}
        </span>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemoveModule(module.id)}
          className="h-8 w-8 text-brand-terracotta-bright hover:text-brand-terracotta-bright"
        >
          <Trash2 size={16} />
        </Button>
      </div>

      {/* Media Upload */}
      <div>
        <label className="text-sm uppercase text-primary mb-2 block">
          MEDIA <span className="text-primary">*</span>
        </label>
        {module.videoPreview ? (
          <div className="relative">
            {module.mediaType === "AUDIO" ? (
              <div className="border border-input rounded-md p-4 bg-card/30">
                <div className="flex items-center gap-3 mb-3">
                  <Disc className="h-8 w-8 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-primary truncate">
                      {module.title || "Audio"}
                    </p>
                    <p className="text-xs text-body">{module.duration || "Audio"}</p>
                  </div>
                </div>
                <audio src={module.videoPreview} controls className="w-full" />
              </div>
            ) : (
              <video
                src={module.videoPreview}
                className="w-full max-h-80 object-contain rounded-md bg-transparent"
                controls
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemoveVideo(module.id)}
              className="absolute top-2 right-2 h-8 w-8 bg-background/80 hover:bg-background"
            >
              <Trash2 size={16} className="text-brand-terracotta-bright" />
            </Button>
          </div>
        ) : (
          <div
            className={cn(
              "border-1 border-dashed rounded-md py-8 text-center cursor-pointer transition-colors",
              errors?.video
                ? "border-brand-terracotta-bright hover:border-brand-terracotta-bright"
                : "border-input hover:border-primary",
            )}
            onClick={() => {
              currentModuleIdRef.current = module.id
              videoInputRef.current?.click()
            }}
          >
            <Disc className="mx-auto mb-2 h-8 w-8 text-body" />
            <p className="text-sm text-body">{t("courseModules.uploadVideo")}</p>
          </div>
        )}
        {errors?.video && (
          <p className="text-xs text-brand-terracotta-bright mt-1">{errors.video}</p>
        )}

        {/* Thumbnail Section */}
        {module.videoPreview ? (
          <div className="mt-3">
            <label className="text-sm text-primary mb-2 block">
              {t("courseModules.coverImage")}
            </label>
            {module.thumbnailPreview ? (
              <div className="relative">
                <img
                  src={module.thumbnailPreview}
                  alt={`Cover for ${module.title || `Module ${index + 1}`}`}
                  className="w-full max-h-48 object-cover rounded-lg border border-border"
                />
                <div className="absolute top-2 right-2 flex gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t("courseModules.changeCover")}
                    onClick={() => {
                      currentThumbnailModuleIdRef.current = module.id
                      thumbnailInputRef.current?.click()
                    }}
                    className="h-8 bg-white !text-[#020202] text-xs font-medium shadow-sm border-gray-300"
                  >
                    <ImagePlus size={14} className="mr-1.5 !text-[#020202]" />
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t("courseModules.removeCover")}
                    onClick={() =>
                      onUpdateModule(module.id, {
                        thumbnailFile: undefined,
                        thumbnailPreview: undefined,
                        removeThumbnail: true,
                      })
                    }
                    className="h-8 bg-white !text-[#020202] text-xs font-medium shadow-sm border-gray-300"
                  >
                    <X size={14} className="mr-1 !text-[#020202]" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                aria-label={t("courseModules.addCover")}
                onClick={() => {
                  currentThumbnailModuleIdRef.current = module.id
                  thumbnailInputRef.current?.click()
                }}
                className="w-full border-2 border-dashed border-input rounded-lg py-6 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <ImagePlus className="mx-auto mb-2 h-8 w-8 text-body" />
                <p className="text-sm text-body">Add cover image</p>
                <p className="text-xs text-muted-foreground mt-1">PNG or JPEG, max 10MB</p>
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* Title */}
      <CustomInput
        value={module.title}
        onChange={e => onUpdateModule(module.id, "title", e.target.value)}
        id={`module-title-${module.id}`}
        label="MODULE TITLE"
        required
        placeholder={t("courseModules.titlePlaceholder")}
        error={errors?.title}
        onClearError={() => {}}
      />

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm uppercase text-primary">
          DESCRIPTION <span className="text-primary">*</span>
        </label>
        <textarea
          value={module.description}
          onChange={e => onUpdateModule(module.id, "description", e.target.value)}
          rows={3}
          placeholder={t("courseModules.descriptionPlaceholder")}
          className={cn(
            "w-full border-b border-input bg-transparent py-2 text-sm focus:border-primary focus:outline-none resize-none",
            errors?.description && "border-brand-terracotta-bright",
          )}
        />
        {errors?.description && (
          <p className="text-xs text-brand-terracotta-bright mt-1">{errors.description}</p>
        )}
      </div>
    </div>
  )
}

export function CourseModulesSection({
  modules,
  onAddModule,
  onRemoveModule,
  onReorderModules,
  onUpdateModule,
  moduleErrors,
  sectionError,
  thumbnailFiles: _thumbnailFiles,
}: CourseModulesSectionProps) {
  const { t } = useTranslation("rituals")
  const videoInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const currentModuleIdRef = useRef<string | null>(null)
  const currentThumbnailModuleIdRef = useRef<string | null>(null)
  const videoBlobUrlsRef = useRef<Map<string, string>>(new Map())
  const thumbnailBlobUrlsRef = useRef<Map<string, string>>(new Map())

  // Setup dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Helper function to get video duration from file
  const getMediaDuration = (file: File): Promise<number> => {
    return new Promise(resolve => {
      const isAudio = file.type.startsWith("audio/")
      const el = document.createElement(isAudio ? "audio" : "video")
      el.preload = "metadata"
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src)
        resolve(Math.round(el.duration))
      }
      el.onerror = () => {
        resolve(0)
      }
      el.src = URL.createObjectURL(file)
    })
  }

  const handleVideoUpload = async (moduleId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isAudio = file.type.startsWith("audio/")
    const mediaType = isAudio ? "AUDIO" : "VIDEO"

    // Get limits based on media type
    const limits = isAudio ? COURSE_MODULE_MEDIA_LIMITS.AUDIO : COURSE_MODULE_MEDIA_LIMITS.VIDEOS

    // Validate file type (extension fallback for Android's empty/generic MIME).
    const allowedTypes = [...limits.ALLOWED_TYPES] as readonly string[]
    if (!isFileTypeAllowed(file, allowedTypes)) {
      const allowedExts = allowedTypes.map(t => t.split("/")[1].toUpperCase()).join(", ")
      toast.error(`Invalid file type. Allowed: ${allowedExts}`)
      if (videoInputRef.current) videoInputRef.current.value = ""
      return
    }

    // Validate file size
    if (file.size > limits.MAX_FILE_SIZE) {
      const maxSizeMB = limits.MAX_FILE_SIZE / (1024 * 1024)
      toast.error(`File exceeds ${maxSizeMB}MB limit`)
      if (videoInputRef.current) videoInputRef.current.value = ""
      return
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file)
    videoBlobUrlsRef.current.set(moduleId, previewUrl)

    // Get duration in seconds
    const durationInSeconds = await getMediaDuration(file)

    // Update all fields at once to avoid stale state
    onUpdateModule(moduleId, {
      videoFile: file,
      videoPreview: previewUrl,
      duration: `${durationInSeconds} sec`,
      mediaType,
    })

    // Reset input
    if (videoInputRef.current) {
      videoInputRef.current.value = ""
    }
  }

  const handleRemoveVideo = (moduleId: string) => {
    const previewUrl = videoBlobUrlsRef.current.get(moduleId)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      videoBlobUrlsRef.current.delete(moduleId)
    }
    // Also clean up thumbnail
    const thumbUrl = thumbnailBlobUrlsRef.current.get(moduleId)
    if (thumbUrl) {
      URL.revokeObjectURL(thumbUrl)
      thumbnailBlobUrlsRef.current.delete(moduleId)
    }
    onUpdateModule(moduleId, {
      videoFile: undefined,
      videoPreview: "",
      duration: "",
      mediaType: undefined,
      thumbnailFile: undefined,
      thumbnailPreview: undefined,
    })
  }

  const handleThumbnailUpload = (moduleId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = [...IMAGE_LIMITS.ALLOWED_TYPES] as readonly string[]
    if (!allowedTypes.includes(file.type)) {
      toast.error("Thumbnail must be a PNG or JPEG image")
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = ""
      return
    }

    // Validate file size
    if (file.size > THUMBNAIL_LIMITS.MAX_FILE_SIZE) {
      const maxSizeMB = THUMBNAIL_LIMITS.MAX_FILE_SIZE / (1024 * 1024)
      toast.error(`Thumbnail exceeds ${maxSizeMB}MB limit`)
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = ""
      return
    }

    // Clean up old thumbnail blob URL
    const oldUrl = thumbnailBlobUrlsRef.current.get(moduleId)
    if (oldUrl) {
      URL.revokeObjectURL(oldUrl)
    }

    const previewUrl = URL.createObjectURL(file)
    thumbnailBlobUrlsRef.current.set(moduleId, previewUrl)

    onUpdateModule(moduleId, {
      thumbnailFile: file,
      thumbnailPreview: previewUrl,
      removeThumbnail: false,
    })

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = ""
    }
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = modules.findIndex(m => m.id === active.id)
    const newIndex = modules.findIndex(m => m.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    // Reorder modules
    const newModules = [...modules]
    const [removed] = newModules.splice(oldIndex, 1)
    newModules.splice(newIndex, 0, removed)

    // Update order values
    const reorderedModules = newModules.map((module, index) => ({
      ...module,
      order: index,
    }))

    onReorderModules(reorderedModules)
  }

  const cleanupVideoUrls = () => {
    for (const url of videoBlobUrlsRef.current.values()) {
      URL.revokeObjectURL(url)
    }
    videoBlobUrlsRef.current.clear()
    for (const url of thumbnailBlobUrlsRef.current.values()) {
      URL.revokeObjectURL(url)
    }
    thumbnailBlobUrlsRef.current.clear()
  }

  // Cleanup on unmount
  useState(() => {
    return () => cleanupVideoUrls()
  })

  return (
    <div className="space-y-4">
      {/* Section Error Message */}
      {sectionError && <p className="text-sm text-brand-terracotta-bright">{sectionError}</p>}

      <input
        ref={videoInputRef}
        type="file"
        accept={COURSE_MODULE_ACCEPT}
        className="hidden"
        onChange={e => {
          if (currentModuleIdRef.current) {
            handleVideoUpload(currentModuleIdRef.current, e)
            currentModuleIdRef.current = null
          }
        }}
      />

      <input
        ref={thumbnailInputRef}
        type="file"
        accept={IMAGE_LIMITS.ALLOWED_TYPES.join(",")}
        className="hidden"
        onChange={e => {
          if (currentThumbnailModuleIdRef.current) {
            handleThumbnailUpload(currentThumbnailModuleIdRef.current, e)
            currentThumbnailModuleIdRef.current = null
          }
        }}
      />

      {modules.length === 0 ? (
        <div
          className="border-1 border-dashed border-primary rounded-md py-12 text-center cursor-pointer"
          onClick={onAddModule}
        >
          <Video className="mx-auto mb-2 h-10 w-10 text-primary" />
          <p className="text-sm text-body">{t("courseModules.addFirst")}</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {modules.map((module, index) => (
                <SortableModule
                  key={module.id}
                  module={module}
                  index={index}
                  onRemoveModule={onRemoveModule}
                  onRemoveVideo={handleRemoveVideo}
                  onUpdateModule={onUpdateModule}
                  videoInputRef={videoInputRef}
                  thumbnailInputRef={thumbnailInputRef}
                  currentModuleIdRef={currentModuleIdRef}
                  currentThumbnailModuleIdRef={currentThumbnailModuleIdRef}
                  errors={moduleErrors?.[module.id]}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Module Button */}
      <Button
        type="button"
        variant="ghost"
        onClick={onAddModule}
        className="flex items-center gap-2 text-sm font-medium text-body w-full py-3 border border-dashed border-input rounded-lg hover:border-primary"
      >
        <Plus size={18} />
        Add Module
      </Button>
    </div>
  )
}
